import type { TripData } from '../types';

function day(
  date: string,
  title: string,
  opts: Partial<{
    summary: string;
    location: string;
    items: TripData['days'][0]['items'];
    todos: TripData['days'][0]['todos'];
  }> = {},
) {
  return {
    date,
    title,
    summary: opts.summary,
    location: opts.location,
    items: opts.items ?? [],
    todos: opts.todos ?? [],
  };
}

export const SEED: TripData = {
  version: 11,
  meta: {
    name: 'Chile · Torres del Paine',
    region: 'Patagonia & Atacama, Chile',
    startDate: '2026-11-14',
    endDate: '2026-12-03',
    travelers: [
      {
        id: 'michael',
        name: 'M',
        shortName: 'M',
      },
      {
        id: 'suzy',
        name: 'S',
        shortName: 'S',
      },
    ],
    notes: [
      'W trek west → east (Paine Grande → Cuernos → Chileno → Laguna Amarga).',
      'Park tickets: may still need — https://www.pasesparques.cl/en',
      'Las Torres: pre check-in via operator portal (ref not stored publicly).',
      'Booked lodging: Hyatt Place Santiago/Vitacura Nov 15–20 and Dec 1–2.',
      'Atacama Nov 20–24 is PROPOSED / not booked — flights, San Pedro lodging, tours, and transfers still TBD.',
      'Gaps TBD: S flights; San Pedro & Puerto Natales lodging; Atacama flights/tours; exact PN→SCL flight on Nov 30.',
      'Optional attractions live in Ideas — vote 👍/👎 then drag winners onto day schedules.',
    ],
  },
  days: [
    day('2026-11-14', 'Depart SFO', {
      summary: 'M: SFO → LAX → SCL (overnight). S flights TBD.',
      location: 'SFO / LAX',
      items: [
        {
          id: 'f1',
          time: '09:55',
          endTime: '11:30',
          title: 'DL1421 SFO → LAX',
          detail: 'Arrives LAX 11:27',
          category: 'flight',

          travelers: ['michael'],
          status: 'confirmed',
        },
        {
          id: 'f2',
          time: '13:55',
          endTime: '05:35',
          title: 'DL6012 LATAM LAX → SCL',
          detail: 'Arrives Santiago 05:35 on 15 Nov · overnight',
          category: 'flight',

          travelers: ['michael'],
          status: 'confirmed',
        },
        {
          id: 'suzy-flight-out',
          title: 'S outbound flights',
          detail: 'Not booked yet — add when confirmed',
          category: 'flight',
          travelers: ['suzy'],
          status: 'tbd',
        },
      ],
      todos: [
        { id: 't-141-1', text: 'Confirm S outbound flights', done: false },
        { id: 't-141-2', text: 'Download offline maps / boarding passes', done: false },
      ],
    }),
    day('2026-11-15', 'Arrive Santiago · Hyatt Vitacura', {
      summary:
        'M lands SCL ~05:35. Check in Hyatt Place Santiago/Vitacura (booked through Nov 20). Soft evening proposed after rest.',
      location: 'Santiago · Vitacura',
      items: [
        {
          id: 'arrive-scl',
          time: '05:35',
          title: 'Land Santiago (SCL)',
          detail: 'DL6012 / LATAM from LAX',
          category: 'flight',
          travelers: ['michael'],
          status: 'confirmed',
        },
        {
          id: 'hyatt-checkin-nov15',
          title: 'Hyatt Place Santiago/Vitacura',
          detail:
            'Check-in Nov 15 → check-out Nov 20 · Hyatt Place King (sofa bed)',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-hyatt-vitacura',
        },
        {
          id: 'transfer-scl-hyatt',
          time: 'morning',
          title: 'Transfer SCL → Hyatt Vitacura',
          detail: 'PROPOSED · taxi / rideshare / hotel transfer · ~30–45 min',
          category: 'transport',
          status: 'proposed',
        },
        {
          id: 'coffee-bicka-15',
          time: 'morning',
          title: 'Bicka (coffee)',
          detail: 'PROPOSED · Av. Luis Pasteur 6156, Vitacura · specialty roaster café · early weekday hours · walk/Uber from Hyatt',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-bicka',
        },
        {
          id: 'rest-arrive-15',
          time: 'afternoon',
          title: 'Rest & settle after overnight flight',
          detail: 'PROPOSED · hydrate, light lunch near hotel, no big plans',
          category: 'note',
          status: 'proposed',
        },
        {
          id: 'evening-vitacura-15',
          time: 'evening',
          title: 'Easy dinner in Vitacura / Las Condes',
          detail: 'PROPOSED · walkable dinner near Hyatt; early night',
          category: 'food',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-151-1', text: 'Plan transfer from SCL airport to Hyatt Vitacura', done: false },
        { id: 't-151-2', text: 'Confirm S arrival timing / meet-up', done: false },
      ],
    }),
    day('2026-11-16', 'Santiago · city & viewpoints', {
      summary:
        'Santiago free day at Hyatt Vitacura. Proposed: Cerro San Cristóbal + barrio Lastarria evening. Vote on Ideas for swaps.',
      location: 'Santiago · Vitacura',
      items: [
        {
          id: 'hyatt-stay-16',
          title: 'Hyatt Place Santiago/Vitacura',
          detail: 'Night of stay · King + sofa bed',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-hyatt-vitacura',
        },
        {
          id: 'work-bicka-16',
          time: 'morning',
          title: 'Work at Bicka',
          detail: 'Cafe work block · laptop hours · proposed',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-bicka',
          questLabel: 'Work',
        },
        {
          id: 'coffee-puelo-16',
          time: 'morning',
          title: 'Puelo Specialty Coffee Bar',
          detail: 'PROPOSED · Las Catalpas 1522, Vitacura · brunch + specialty coffee · near Hyatt',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-puelo',
        },
        {
          id: 'cerro-san-cristobal',
          time: 'morning',
          title: 'Cerro San Cristóbal / Parque Metropolitano',
          detail: 'PROPOSED · funicular or hike · city views · sunscreen & water',
          category: 'attraction',
          status: 'proposed',
          pinId: 'pin-cerro-san-cristobal',
        },
        {
          id: 'lunch-bellavista-16',
          time: 'afternoon',
          title: 'Lunch near Bellavista / Pio Nono',
          detail: 'PROPOSED · casual lunch after the hill',
          category: 'food',
          status: 'proposed',
          pinId: 'pin-bellavista',
        },
        {
          id: 'lastarria-evening-16',
          time: 'evening',
          title: 'Barrio Lastarria stroll & dinner',
          detail: 'PROPOSED · galleries, cafés, dinner · Metro Universidad Católica area',
          category: 'attraction',
          status: 'proposed',
          pinId: 'pin-lastarria',
        },
      ],
      todos: [
        { id: 't-161-1', text: 'Decide Cerro San Cristóbal (funicular vs hike)', done: false },
      ],
    }),
    day('2026-11-17', 'Santiago · markets & museums', {
      summary:
        'Santiago free day. Proposed Mercado Central + Bellas Artes; optional wine valley if energy is high (see Ideas).',
      location: 'Santiago · Vitacura',
      items: [
        {
          id: 'hyatt-stay-17',
          title: 'Hyatt Place Santiago/Vitacura',
          detail: 'Night of stay · King + sofa bed',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-hyatt-vitacura',
        },
        {
          id: 'work-puelo-17',
          time: 'morning',
          title: 'Work at Puelo',
          detail: 'Cafe work block · laptop hours · proposed',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-puelo',
          questLabel: 'Work',
        },
        {
          id: 'coffee-super-delicious-17',
          time: 'morning',
          title: 'Super Delicious',
          detail: 'PROPOSED · Av. Vitacura 2839, Las Condes · specialty coffee bar · before Mercado Central',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-super-delicious',
        },
        {
          id: 'mercado-central',
          time: 'morning',
          title: 'Mercado Central',
          detail: 'PROPOSED · seafood market & lunch · go early to beat crowds',
          category: 'food',
          status: 'proposed',
          pinId: 'pin-mercado-central',
        },
        {
          id: 'museo-bellas-artes',
          time: 'afternoon',
          title: 'Museo Nacional de Bellas Artes',
          detail: 'PROPOSED · Parque Forestal · combine with Lastarria walk',
          category: 'attraction',
          status: 'proposed',
          pinId: 'pin-bellas-artes',
        },
        {
          id: 'evening-santiago-17',
          time: 'evening',
          title: 'Free evening · Vitacura or Providencia',
          detail: 'PROPOSED · low-key dinner; or vote Casablanca wine day instead',
          category: 'open',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-171-1', text: 'Vote: Mercado/Bellas Artes vs Casablanca wine day', done: false },
      ],
    }),
    day('2026-11-18', 'Santiago · Cajón del Maipo (proposed)', {
      summary:
        'Santiago free day. Proposed day trip to Cajón del Maipo (nature / Embalse El Yeso if roads OK). Swap via Ideas if preferred.',
      location: 'Santiago · Cajón del Maipo',
      items: [
        {
          id: 'hyatt-stay-18',
          title: 'Hyatt Place Santiago/Vitacura',
          detail: 'Night of stay · King + sofa bed',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-hyatt-vitacura',
        },
        {
          id: 'work-super-18',
          time: 'morning',
          title: 'Work at Super Delicious',
          detail: 'Cafe work block · laptop hours · proposed',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-super-delicious',
          questLabel: 'Work',
        },
        {
          id: 'coffee-singular-18',
          time: 'morning',
          title: 'Singular Coffee Roasters Vitacura',
          detail: 'PROPOSED · Av. Las Condes 11700 (mall Loc.102A) · Chilean roastery café · early coffee before Cajón del Maipo',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-singular',
        },
        {
          id: 'cajon-maipo',
          time: 'morning',
          title: 'Cajón del Maipo day trip',
          detail:
            'PROPOSED · not booked · rental car or tour · check road conditions for El Yeso',
          category: 'attraction',
          status: 'proposed',
          pinId: 'pin-cajon-maipo',
        },
        {
          id: 'lunch-maipo-18',
          time: 'afternoon',
          title: 'Lunch in San José de Maipo / valley',
          detail: 'PROPOSED · mountain-town lunch on the way back',
          category: 'food',
          status: 'proposed',
        },
        {
          id: 'evening-rest-18',
          time: 'evening',
          title: 'Return to Hyatt · easy evening',
          detail: 'PROPOSED · recover before final Santiago day',
          category: 'note',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-181-1', text: 'Book Cajón del Maipo tour or rental car (if keeping this day)', done: false },
        { id: 't-181-2', text: 'Check Embalse El Yeso access / weather', done: false },
      ],
    }),
    day('2026-11-19', 'Santiago · last city day', {
      summary:
        'Last full Santiago day before Atacama. Pack for altitude; optional Maipo or Casablanca wine if not done earlier.',
      location: 'Santiago · Vitacura',
      items: [
        {
          id: 'hyatt-stay-19',
          title: 'Hyatt Place Santiago/Vitacura',
          detail: 'Final night of Nov stay · King + sofa bed',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-hyatt-vitacura',
        },
        {
          id: 'work-singular-19',
          time: 'morning',
          title: 'Work at Singular',
          detail: 'Cafe work block · laptop hours · proposed',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-singular',
          questLabel: 'Work',
        },
        {
          id: 'coffee-colmado-19',
          time: 'morning',
          title: 'Colmado Coffee (Lastarria)',
          detail: 'PROPOSED · Merced 346, Barrio Lastarria · specialty coffee & bakery · city-day option',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-colmado',
        },
        {
          id: 'coffee-trieste-19',
          time: 'morning',
          title: 'Café Trieste (Lastarria)',
          detail: 'PROPOSED · Padre Luis de Valdivia 339, Barrio Lastarria · Illy café · alternate city-day coffee (Café Journal is in Viña del Mar, not Santiago)',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-cafe-trieste',
        },
        {
          id: 'optional-wine-19',
          time: 'morning',
          title: 'Optional: Maipo or Casablanca wine tasting',
          detail: 'PROPOSED · only if not done Nov 17–18 · half-day tour TBD',
          category: 'attraction',
          status: 'proposed',
          pinId: 'pin-casablanca',
        },
        {
          id: 'pack-atacama-19',
          time: 'afternoon',
          title: 'Pack for Atacama',
          detail: 'PROPOSED · layers, sunscreen, water bottle, altitude meds if any',
          category: 'note',
          status: 'proposed',
        },
        {
          id: 'early-dinner-19',
          time: 'evening',
          title: 'Early dinner · prep for morning flight',
          detail: 'PROPOSED · near Hyatt; confirm SCL→CJC timing',
          category: 'food',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-191-1', text: 'Pack for Atacama / altitude (layers, sunscreen, water)', done: false },
        { id: 't-191-2', text: 'Confirm SCL→CJC flight booking for Nov 20', done: false },
      ],
    }),
    day('2026-11-20', 'PROPOSED · Fly to Atacama', {
      summary:
        'PROPOSED (not booked): Checkout Hyatt Vitacura → fly SCL→CJC (Calama) → transfer to San Pedro de Atacama. Easy evening to acclimatize.',
      location: 'Santiago → Calama → San Pedro de Atacama',
      items: [
        {
          id: 'hyatt-checkout-20',
          title: 'Checkout Hyatt Place Vitacura',
          detail: 'End of Nov 15–20 stay',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-hyatt-vitacura',
        },
        {
          id: 'flight-scl-cjc',
          title: 'Fly SCL → CJC (Calama)',
          detail: 'PROPOSED · not booked yet',
          category: 'flight',
          status: 'proposed',
        },
        {
          id: 'transfer-cjc-sp',
          title: 'Transfer CJC → San Pedro de Atacama',
          detail: 'PROPOSED · shared shuttle or private transfer (~1.5–2h)',
          category: 'transport',
          status: 'proposed',
        },
        {
          id: 'sp-lodging-20',
          title: 'San Pedro lodging',
          detail: 'PROPOSED guesthouse/hotel · TBD · not booked · nights Nov 20–23/24',
          category: 'lodging',
          status: 'proposed',
          pinId: 'pin-san-pedro',
        },
        {
          id: 'acclimate-20',
          time: 'evening',
          title: 'Easy evening · acclimatize',
          detail: 'Light walk in town; hydrate; avoid heavy exertion at altitude',
          category: 'note',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-201-1', text: 'Book SCL–CJC flight for Nov 20', done: false },
        { id: 't-201-2', text: 'Book San Pedro lodging Nov 20–23/24', done: false },
        { id: 't-201-3', text: 'Arrange CJC → San Pedro transfer', done: false },
      ],
    }),
    day('2026-11-21', 'PROPOSED · Valle de la Luna', {
      summary:
        'PROPOSED (not booked): Acclimatize + Valle de la Luna (afternoon/sunset). Optional coffee near Caracoles.',
      location: 'San Pedro de Atacama',
      items: [
        {
          id: 'quitor',
          title: 'Pukará de Quitor (optional morning)',
          detail: 'PROPOSED · short acclimatization outing near town',
          category: 'attraction',
          status: 'proposed',
        },
        {
          id: 'valle-luna',
          time: 'afternoon',
          title: 'Valle de la Luna (sunset)',
          detail: 'PROPOSED · book tour or self-drive; golden hour / sunset',
          category: 'attraction',
          status: 'proposed',
        },
        {
          id: 'coffee-sp',
          title: 'Optional coffee near Caracoles',
          detail: 'Emporio Andino · Roots Café · Café Adobe',
          category: 'coffee',
          status: 'proposed',
          pinId: 'pin-emporio',
        },
        {
          id: 'sp-lodging-21',
          title: 'San Pedro lodging',
          detail: 'PROPOSED · TBD · not booked',
          category: 'lodging',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-211-1', text: 'Book Valle de la Luna / Moon Valley tour', done: false },
      ],
    }),
    day('2026-11-22', 'PROPOSED · El Tatio', {
      summary:
        'PROPOSED (not booked): Early El Tatio geysers (~4300m); rest afternoon; optional Puritama hot springs.',
      location: 'El Tatio · San Pedro',
      items: [
        {
          id: 'el-tatio',
          time: 'early',
          title: 'El Tatio geysers',
          detail: 'PROPOSED · ~4300m · very early departure; layers + water essential',
          category: 'attraction',
          status: 'proposed',
        },
        {
          id: 'rest-22',
          time: 'afternoon',
          title: 'Rest / recover in San Pedro',
          detail: 'Altitude day — take it easy after Tatio',
          category: 'note',
          status: 'proposed',
        },
        {
          id: 'puritama',
          title: 'Optional: Termas de Puritama',
          detail: 'PROPOSED · hot springs if energy allows',
          category: 'attraction',
          status: 'proposed',
        },
        {
          id: 'sp-lodging-22',
          title: 'San Pedro lodging',
          detail: 'PROPOSED · TBD · not booked',
          category: 'lodging',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-221-1', text: 'Book El Tatio geyser tour', done: false },
        { id: 't-221-2', text: 'Decide on Puritama (add-on or separate)', done: false },
      ],
    }),
    day('2026-11-23', 'PROPOSED · Salt flats', {
      summary:
        'PROPOSED (not booked): Laguna Chaxa / Salar de Atacama (+ optional Toconao). Safer exit: fly toward SCL this day if protecting Nov 25 trek.',
      location: 'Salar de Atacama · San Pedro',
      items: [
        {
          id: 'chaxa',
          title: 'Laguna Chaxa / Salar de Atacama',
          detail: 'PROPOSED · flamingos & salt flats tour',
          category: 'attraction',
          status: 'proposed',
        },
        {
          id: 'tebenquiche',
          title: 'Optional: Laguna Tebenquiche',
          detail: 'PROPOSED · often combined with salt-flat circuits',
          category: 'attraction',
          status: 'proposed',
        },
        {
          id: 'toconao',
          title: 'Optional: Toconao village',
          detail: 'PROPOSED · short stop if on the circuit',
          category: 'attraction',
          status: 'proposed',
        },
        {
          id: 'safer-exit-note',
          title: 'Safer exit option',
          detail:
            'If protecting Nov 25 06:50 bus: fly toward SCL this day instead of waiting for Nov 24 same-day CJC→SCL→PNT',
          category: 'note',
          status: 'open',
        },
        {
          id: 'sp-lodging-23',
          title: 'San Pedro lodging (if still in town)',
          detail: 'PROPOSED · TBD · not booked · or depart toward SCL tonight',
          category: 'lodging',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-231-1', text: 'Book salt flat / Laguna Chaxa tour', done: false },
        {
          id: 't-231-2',
          text: 'Decide safer exit: fly CJC→SCL on Nov 23 vs Nov 24 tight connection',
          done: false,
        },
        { id: 't-231-3', text: 'Book CJC–SCL–PNT (or staged) for Nov 23/24', done: false },
      ],
    }),
    day('2026-11-24', 'PROPOSED · Travel to Puerto Natales', {
      summary:
        'PROPOSED (not booked): Travel day toward Puerto Natales (CJC→SCL→PNT); overnight PN. RISK: same-day tight connection — arriving PN morning of Nov 25 is unsafe for 06:50 bus.',
      location: 'Calama → Santiago → Puerto Natales',
      items: [
        {
          id: 'flight-cjc-scl-pnt',
          title: 'Fly CJC → SCL → PNT',
          detail:
            'PROPOSED · not booked · FLAG: same-day tight connection risk; prefer overnight buffer in SCL or arrive PN on Nov 23 evening',
          category: 'flight',
          status: 'proposed',
        },
        {
          id: 'pn-lodging-24',
          title: 'Puerto Natales lodging',
          detail: 'PROPOSED · TBD · not booked · night before W trek',
          category: 'lodging',
          status: 'proposed',
        },
        {
          id: 'connection-risk',
          title: 'Connection risk warning',
          detail:
            'Arriving Puerto Natales morning of Nov 25 is unsafe for the 06:50 Recorrido bus — build buffer',
          category: 'note',
          status: 'open',
        },
      ],
      todos: [
        { id: 't-241-1', text: 'Book Puerto Natales lodging for Nov 24', done: false },
        { id: 't-241-2', text: 'Lock CJC→SCL→PNT itinerary with safe buffer', done: false },
        { id: 't-241-3', text: 'Arrange any remaining CJC transfers', done: false },
      ],
    }),
    day('2026-11-25', 'Enter park · W trek day 1', {
      summary: 'Bus PN → Pudeto, catamaran to Paine Grande, night Vertice.',
      location: 'Puerto Natales → Paine Grande',
      items: [
        {
          id: 'bus-pudeto',
          time: '06:50',
          endTime: '10:00',
          title: 'Bus Recorrido → Pudeto',
          detail: 'Puerto Natales 06:50 → Pudeto 09:30',
          category: 'transport',

          status: 'confirmed',
        },
        {
          id: 'catamaran',
          time: '10:30',
          endTime: '11:00',
          title: 'Catamaran Pudeto → Paine Grande',
          detail: 'Booking Patagonia · arrives ~11:00',
          category: 'transport',

          status: 'confirmed',
        },
        {
          id: 'trek-d1',
          time: '11:00',
          endTime: '16:00',
          title: 'W trek · settle / short hike',
          detail: 'West → east start at Paine Grande',
          category: 'trek',
          status: 'confirmed',
        },
        {
          id: 'vertice',
          time: 'evening',
          title: 'Night: Vertice Paine Grande',
          detail: 'Vertice.travel paid · exact room TBD',
          category: 'lodging',
          status: 'confirmed',
        },
        {
          id: 'park-tickets',
          title: 'Park tickets',
          detail: 'May still need — check pasesparques.cl',
          category: 'open',
          link: 'https://www.pasesparques.cl/en',
          status: 'open',
        },
      ],
      todos: [
        { id: 't-251-1', text: 'Confirm / buy park tickets (pasesparques.cl)', done: false },
        { id: 't-251-2', text: 'Pack daypack for catamaran + first night', done: false },
        { id: 't-251-3', text: 'Confirm Vertice room assignment', done: false },
      ],
    }),
    day('2026-11-26', 'W trek · to Cuernos', {
      summary: 'Trek to Campsite Cuernos (Las Torres). Fully-equipped double.',
      location: 'Paine Grande → Cuernos',
      items: [
        {
          id: 'trek-cuernos',
          title: 'Trek to Cuernos',
          detail: 'Classic W segment along Lake Nordenskjöld',
          category: 'trek',
          status: 'confirmed',
          pinId: 'pin-cuernos',
        },
        {
          id: 'cuernos-night1',
          time: 'evening',
          title: 'Campsite Cuernos',
          detail: 'Las Torres · fully-equipped double · night 1 of 2',
          category: 'lodging',
          status: 'confirmed',
          pinId: 'pin-cuernos',
        },
      ],
      todos: [
        { id: 't-261-1', text: 'Pre check-in Las Torres', done: false },
        { id: 't-261-2', text: 'Water + snacks for Cuernos leg', done: false },
      ],
    }),
    day('2026-11-27', 'W trek · Cuernos → Chileno', {
      summary: 'Second night Cuernos area then on to Chileno (or hike day + move).',
      location: 'Cuernos → Chileno',
      items: [
        {
          id: 'cuernos-morning',
          title: 'Morning at Cuernos',
          detail: 'Checkout / continue east on the W',
          category: 'trek',
          status: 'confirmed',
          pinId: 'pin-cuernos',
        },
        {
          id: 'trek-chileno',
          title: 'Trek toward Chileno / Torres sector',
          detail: 'Approach to Base Torres corridor',
          category: 'trek',
          status: 'confirmed',
          pinId: 'pin-chileno',
        },
        {
          id: 'chileno-night',
          time: 'evening',
          title: 'Campsite Chileno',
          detail: 'Las Torres · fully-equipped double · 27–28 Nov',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-chileno',
        },
      ],
      todos: [
        { id: 't-271-1', text: 'Decide Base Torres timing (dawn vs afternoon)', done: false },
      ],
    }),
    day('2026-11-28', 'Base Torres · exit park', {
      summary: 'Chileno / Base Torres, then bus Laguna Amarga → Puerto Natales.',
      location: 'Chileno → Laguna Amarga → Puerto Natales',
      items: [
        {
          id: 'base-torres',
          title: 'Base Torres hike (optional timing)',
          detail: 'Classic viewpoint from Chileno sector',
          category: 'attraction',
          status: 'confirmed',
          pinId: 'pin-base-torres',
        },
        {
          id: 'bus-exit',
          time: '20:00',
          title: 'Bus Laguna Amarga → Puerto Natales',
          detail: 'Arrives 21:35',
          category: 'transport',

          status: 'confirmed',
        },
        {
          id: 'pn-lodging-28',
          title: 'Puerto Natales lodging',
          detail: 'Not booked yet — arrive ~21:35',
          category: 'lodging',
          status: 'tbd',
        },
      ],
      todos: [
        { id: 't-281-1', text: 'Book Puerto Natales lodging for 28 Nov', done: false },
        { id: 't-281-2', text: 'Confirm Laguna Amarga bus pickup spot', done: false },
      ],
    }),
    day('2026-11-29', 'Puerto Natales · recovery day', {
      summary:
        'Post-trek recovery in Puerto Natales. Lodging still TBD. Proposed waterfront walk + café; optional viewpoints if legs allow.',
      location: 'Puerto Natales',
      items: [
        {
          id: 'pn-lodging-29',
          title: 'Puerto Natales lodging',
          detail: 'Not booked yet · prefer walkable to waterfront / plaza',
          category: 'lodging',
          status: 'tbd',
        },
        {
          id: 'pn-sleep-in-29',
          time: 'morning',
          title: 'Sleep in · laundry / gear dry-out',
          detail: 'PROPOSED · recovery morning after W trek',
          category: 'note',
          status: 'proposed',
        },
        {
          id: 'pn-waterfront-29',
          time: 'afternoon',
          title: 'Puerto Natales waterfront walk',
          detail: 'PROPOSED · malecón views toward fjord / mountains',
          category: 'attraction',
          status: 'proposed',
        },
        {
          id: 'pn-cafe-29',
          time: 'afternoon',
          title: 'Coffee & pastry in town',
          detail: 'PROPOSED · Café Kaiken or similar near plaza',
          category: 'coffee',
          status: 'proposed',
        },
        {
          id: 'pn-dinner-29',
          time: 'evening',
          title: 'Patagonian dinner',
          detail: 'PROPOSED · Asador-style or seafood · celebration meal',
          category: 'food',
          status: 'proposed',
        },
      ],
      todos: [
        { id: 't-291-1', text: 'Book / confirm PN lodging for Nov 28–30', done: false },
        { id: 't-291-2', text: 'Book PNT → SCL flight for Nov 30 (or earlier buffer)', done: false },
      ],
    }),
    day('2026-11-30', 'Puerto Natales → Santiago', {
      summary:
        'PROPOSED travel day: fly Puerto Natales (PNT) → Santiago for Dec 1 Hyatt check-in. Exact flights not booked.',
      location: 'Puerto Natales → Santiago',
      items: [
        {
          id: 'pn-checkout-30',
          time: 'morning',
          title: 'Checkout Puerto Natales lodging',
          detail: 'PROPOSED · timing depends on flight',
          category: 'lodging',
          status: 'proposed',
        },
        {
          id: 'flight-pnt-scl',
          title: 'Fly PNT → SCL',
          detail: 'PROPOSED · not booked · aim to arrive Santiago afternoon/evening',
          category: 'flight',
          status: 'proposed',
        },
        {
          id: 'transfer-scl-hyatt-30',
          time: 'evening',
          title: 'Transfer SCL → Hyatt Vitacura (or nearby buffer stay)',
          detail: 'PROPOSED · Hyatt check-in is Dec 1 — overnight buffer TBD if late arrival',
          category: 'transport',
          status: 'proposed',
        },
        {
          id: 'buffer-note-30',
          title: 'Overnight buffer note',
          detail:
            'If PNT→SCL lands late, may need Dec 1 morning check-in only — or book night of Nov 30 near SCL separately (TBD)',
          category: 'note',
          status: 'open',
        },
      ],
      todos: [
        { id: 't-301-1', text: 'Book PNT→SCL flight for Nov 30', done: false },
        { id: 't-301-2', text: 'Decide Nov 30 overnight: arrive early enough for Dec 1 Hyatt vs extra SCL night', done: false },
      ],
    }),
    day('2026-12-01', 'Santiago · Hyatt Vitacura', {
      summary:
        'Check in Hyatt Place Santiago/Vitacura (overnight before return flights). Soft city evening proposed.',
      location: 'Santiago · Vitacura',
      items: [
        {
          id: 'hyatt-checkin-dec1',
          title: 'Hyatt Place Santiago/Vitacura',
          detail:
            'Check-in Dec 1 → check-out Dec 2 · Hyatt Place King (sofa bed)',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-hyatt-vitacura',
        },
        {
          id: 'arrive-scl-dec1',
          time: 'afternoon',
          title: 'Settle in Santiago',
          detail: 'PROPOSED · unpack trek gear; easy walk near Vitacura',
          category: 'note',
          status: 'proposed',
        },
        {
          id: 'lastarria-or-vitacura-dec1',
          time: 'evening',
          title: 'Dinner · Vitacura or Lastarria',
          detail: 'PROPOSED · celebration / last-night-in-Chile meal',
          category: 'food',
          status: 'proposed',
          pinId: 'pin-lastarria',
        },
      ],
      todos: [
        { id: 't-1201-1', text: 'Confirm arrival time in Santiago for Dec 1 check-in', done: false },
        { id: 't-1201-2', text: 'Confirm S return flight timing vs Dec 2 departure', done: false },
      ],
    }),
    day('2026-12-02', 'Depart Santiago', {
      summary:
        'Checkout Hyatt Vitacura. Free day until overnight SCL → LAX. S return TBD.',
      location: 'Santiago (SCL)',
      items: [
        {
          id: 'hyatt-checkout-dec2',
          title: 'Checkout Hyatt Place Vitacura',
          detail: 'End of Dec 1–2 stay',
          category: 'lodging',

          status: 'confirmed',
          pinId: 'pin-hyatt-vitacura',
        },
        {
          id: 'luggage-storage-dec2',
          time: 'morning',
          title: 'Luggage storage / late checkout if available',
          detail: 'PROPOSED · ask Hyatt; free day before late flight',
          category: 'note',
          status: 'proposed',
        },
        {
          id: 'optional-santiago-dec2',
          time: 'afternoon',
          title: 'Optional light Santiago activity',
          detail: 'PROPOSED · café, short park walk, or souvenir shopping — keep easy before overnight flight',
          category: 'open',
          status: 'proposed',
        },
        {
          id: 'airport-transfer-dec2',
          time: 'evening',
          title: 'Transfer to SCL airport',
          detail: 'PROPOSED · leave buffer for international check-in',
          category: 'transport',
          status: 'proposed',
        },
        {
          id: 'f-return-1',
          time: '23:55',
          endTime: '05:30',
          title: 'DL6011 LATAM SCL → LAX',
          detail: 'Arrives LAX 06:15 on 3 Dec',
          category: 'flight',

          travelers: ['michael'],
          status: 'confirmed',
        },
        {
          id: 'suzy-return',
          title: 'S return flights',
          detail: 'Not booked yet',
          category: 'flight',
          travelers: ['suzy'],
          status: 'tbd',
        },
      ],
      todos: [
        { id: 't-1202-1', text: 'Confirm S return itinerary', done: false },
        { id: 't-1202-2', text: 'Airport transfer to SCL', done: false },
      ],
    }),
    day('2026-12-03', 'Home to SFO', {
      summary: 'LAX → SFO for M.',
      location: 'LAX → SFO',
      items: [
        {
          id: 'f-return-2',
          time: '06:15',
          endTime: '08:40',
          title: 'Land LAX',
          detail: 'From DL6011',
          category: 'flight',
          travelers: ['michael'],
          status: 'confirmed',
        },
        {
          id: 'f-return-3',
          time: '09:25',
          endTime: '11:05',
          title: 'DL2272 LAX → SFO',
          detail: 'Arrives SFO 10:54',
          category: 'flight',

          travelers: ['michael'],
          status: 'confirmed',
        },
      ],
      todos: [],
    }),
  ],
  ideas: [
    {
      id: 'idea-cerro-san-cristobal',
      name: 'Cerro San Cristóbal',
      region: 'Santiago',
      detail: 'Parque Metropolitano · funicular or hike · classic Santiago views',
      category: 'attraction',
      pinId: 'pin-cerro-san-cristobal',
      suggestedDays: ['2026-11-16', '2026-11-17'],
    },
    {
      id: 'idea-mercado-central',
      name: 'Mercado Central',
      region: 'Santiago',
      detail: 'Seafood market & lunch hall · go early',
      category: 'food',
      pinId: 'pin-mercado-central',
      suggestedDays: ['2026-11-17'],
    },
    {
      id: 'idea-bellas-artes',
      name: 'Museo Nacional de Bellas Artes',
      region: 'Santiago',
      detail: 'Parque Forestal · easy combine with Lastarria',
      category: 'attraction',
      pinId: 'pin-bellas-artes',
      suggestedDays: ['2026-11-17'],
    },
    {
      id: 'idea-lastarria',
      name: 'Barrio Lastarria',
      region: 'Santiago',
      detail: 'Galleries, cafés, evening stroll & dinner',
      category: 'attraction',
      pinId: 'pin-lastarria',
      suggestedDays: ['2026-11-16', '2026-11-17', '2026-12-01'],
    },
    {
      id: 'idea-cajon-maipo',
      name: 'Cajón del Maipo day trip',
      region: 'Santiago',
      detail: 'Mountain valley / Embalse El Yeso if roads OK · full day',
      category: 'attraction',
      pinId: 'pin-cajon-maipo',
      suggestedDays: ['2026-11-18'],
    },
    {
      id: 'idea-casablanca-wine',
      name: 'Casablanca Valley wine',
      region: 'Santiago',
      detail: 'Coastal wine valley west of Santiago · half/full day tour',
      category: 'attraction',
      pinId: 'pin-casablanca',
      suggestedDays: ['2026-11-17', '2026-11-19'],
    },
    {
      id: 'idea-maipo-wine',
      name: 'Maipo Valley wine',
      region: 'Santiago',
      detail: 'Closer wine valley south of Santiago · half-day friendly',
      category: 'attraction',
      pinId: 'pin-maipo-wine',
      suggestedDays: ['2026-11-17', '2026-11-19'],
    },
    {
      id: 'idea-pn-waterfront',
      name: 'Puerto Natales waterfront',
      region: 'Puerto Natales',
      detail: 'Malecón walk · post-trek recovery',
      category: 'attraction',
      pinId: 'pin-pn',
      suggestedDays: ['2026-11-29'],
    },
    {
      id: 'idea-pn-viewpoints',
      name: 'Torres viewpoints (if time)',
      region: 'Puerto Natales',
      detail: 'Only if energy remains · short scenic stops near PN, not another full trek day',
      category: 'attraction',
      pinId: 'pin-pn',
      suggestedDays: ['2026-11-29'],
    },
    {
      id: 'idea-valle-luna',
      name: 'Valle de la Luna',
      region: 'Atacama',
      detail: 'Already on proposed Atacama plan · vote to prioritize',
      category: 'attraction',
      pinId: 'pin-valle-luna',
      suggestedDays: ['2026-11-21'],
    },
    {
      id: 'idea-el-tatio',
      name: 'El Tatio geysers',
      region: 'Atacama',
      detail: 'Early high-altitude outing · vote to keep or skip',
      category: 'attraction',
      pinId: 'pin-el-tatio',
      suggestedDays: ['2026-11-22'],
    },
    {
      id: 'idea-puritama',
      name: 'Termas de Puritama',
      region: 'Atacama',
      detail: 'Optional hot springs after Tatio',
      category: 'attraction',
      pinId: 'pin-puritama',
      suggestedDays: ['2026-11-22'],
    },
    {
      id: 'idea-chaxa',
      name: 'Laguna Chaxa / Salar de Atacama',
      region: 'Atacama',
      detail: 'Flamingos & salt flats',
      category: 'attraction',
      pinId: 'pin-chaxa',
      suggestedDays: ['2026-11-23'],
    },
    {
      id: 'idea-puelo',
      name: 'Puelo Specialty Coffee Bar',
      region: 'Santiago',
      detail: 'Las Catalpas 1522, Vitacura · brunch + specialty coffee · PROPOSED',
      category: 'coffee',
      pinId: 'pin-puelo',
      suggestedDays: ['2026-11-16', '2026-11-15', '2026-11-17'],
    },
    {
      id: 'idea-bicka',
      name: 'Bicka',
      region: 'Santiago',
      detail: 'Av. Luis Pasteur 6156, Vitacura · specialty roaster · early weekday hours · PROPOSED',
      category: 'coffee',
      pinId: 'pin-bicka',
      suggestedDays: ['2026-11-15', '2026-11-16'],
    },
    {
      id: 'idea-super-delicious',
      name: 'Super Delicious',
      region: 'Santiago',
      detail: 'Av. Vitacura 2839, Las Condes · specialty coffee bar · PROPOSED',
      category: 'coffee',
      pinId: 'pin-super-delicious',
      suggestedDays: ['2026-11-17', '2026-11-16'],
    },
    {
      id: 'idea-singular',
      name: 'Singular Coffee Roasters Vitacura',
      region: 'Santiago',
      detail: 'Av. Las Condes 11700 Loc.102A · Chilean roastery café · PROPOSED',
      category: 'coffee',
      pinId: 'pin-singular',
      suggestedDays: ['2026-11-18', '2026-11-19'],
    },
    {
      id: 'idea-colmado',
      name: 'Colmado Coffee (Lastarria)',
      region: 'Santiago',
      detail: 'Merced 346, Barrio Lastarria · specialty coffee & bakery · PROPOSED',
      category: 'coffee',
      pinId: 'pin-colmado',
      suggestedDays: ['2026-11-16', '2026-11-17', '2026-11-19'],
    },
    {
      id: 'idea-cafe-trieste',
      name: 'Café Trieste (Lastarria)',
      region: 'Santiago',
      detail: 'Padre Luis de Valdivia 339, Lastarria · Illy café · PROPOSED city-day stop',
      category: 'coffee',
      pinId: 'pin-cafe-trieste',
      suggestedDays: ['2026-11-16', '2026-11-19', '2026-12-01'],
    },
  ],
  votes: {},
  pins: [
    {
      id: 'pin-scl',
      name: 'Santiago Airport (SCL)',
      category: 'flight',
      lat: -33.393,
      lng: -70.786,
      notes: 'Arrival 15 Nov / departure night of 2 Dec',
      dayIds: ['2026-11-15', '2026-12-02'],
      mapsQuery: 'Santiago Airport SCL Chile'
    },
    {
      id: 'pin-hyatt-vitacura',
      name: 'Hyatt Place Santiago/Vitacura',
      category: 'lodging',
      lat: -33.3993,
      lng: -70.5866,
      notes: 'Booked · Nov 15–20 · Dec 1–2 · King + sofa bed',
      dayIds: [
        '2026-11-15',
        '2026-11-16',
        '2026-11-17',
        '2026-11-18',
        '2026-11-19',
        '2026-11-20',
        '2026-12-01',
        '2026-12-02',
      ],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/Hyatt_Place%2C_Vitacura%2C_Santiago_20250804.jpg/960px-Hyatt_Place%2C_Vitacura%2C_Santiago_20250804.jpg',
      imageAlt: 'Exterior of Hyatt Place Santiago/Vitacura hotel',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Hyatt Place Santiago Vitacura Chile'
    },
    {
      id: 'pin-cjc',
      name: 'Calama Airport (CJC)',
      category: 'flight',
      lat: -22.4982,
      lng: -68.9036,
      notes: 'PROPOSED · SCL↔CJC gateway for Atacama · Nov 20 / 23–24',
      dayIds: ['2026-11-20', '2026-11-23', '2026-11-24'],
      mapsQuery: 'Calama Airport CJC Chile'
    },
    {
      id: 'pin-san-pedro',
      name: 'San Pedro de Atacama',
      category: 'lodging',
      lat: -22.9111,
      lng: -68.2011,
      notes: 'PROPOSED base · lodging TBD · Nov 20–23/24',
      dayIds: ['2026-11-20', '2026-11-21', '2026-11-22', '2026-11-23'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/01/Lamas_in_the_sunset_San_Pedro_de_Atacama_Chile_Luca_Galuzzi_2006.jpg/960px-Lamas_in_the_sunset_San_Pedro_de_Atacama_Chile_Luca_Galuzzi_2006.jpg',
      imageAlt: 'Llamas at sunset near San Pedro de Atacama',
      imageCredit: 'Wikimedia Commons · Luca Galuzzi',
      mapsQuery: 'San Pedro de Atacama Chile'
    },
    {
      id: 'pin-quitor',
      name: 'Pukará de Quitor',
      category: 'attraction',
      lat: -22.8908,
      lng: -68.2144,
      notes: 'PROPOSED · acclimatization / short outing',
      dayIds: ['2026-11-21'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a3/Pukar%C3%A1_de_Quitor_o_Pucar%C3%A1_de_Quitor_02.JPG/960px-Pukar%C3%A1_de_Quitor_o_Pucar%C3%A1_de_Quitor_02.JPG',
      imageAlt: 'Stone ruins of Pukará de Quitor overlooking the Atacama valley',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Pukará de Quitor San Pedro de Atacama'
    },
    {
      id: 'pin-valle-luna',
      name: 'Valle de la Luna',
      category: 'attraction',
      lat: -22.9112,
      lng: -68.323,
      notes: 'PROPOSED · afternoon / sunset',
      dayIds: ['2026-11-21'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Valle_de_la_Luna%2C_San_Pedro_de_Atacama%2C_Chile%2C_2016-02-01%2C_DD_169.JPG/960px-Valle_de_la_Luna%2C_San_Pedro_de_Atacama%2C_Chile%2C_2016-02-01%2C_DD_169.JPG',
      imageAlt: 'Desert rock formations in Valle de la Luna near San Pedro de Atacama',
      imageCredit: 'Wikimedia Commons · Diego Delso',
      mapsQuery: 'Valle de la Luna San Pedro de Atacama'
    },
    {
      id: 'pin-emporio',
      name: 'Emporio Andino (coffee)',
      category: 'coffee',
      lat: -22.9106,
      lng: -68.2005,
      notes: 'PROPOSED · near Caracoles · optional coffee',
      dayIds: ['2026-11-21'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5a/Calle_Caracoles%2C_San_Pedro_de_Atacama_-_Chile_-_panoramio.jpg/960px-Calle_Caracoles%2C_San_Pedro_de_Atacama_-_Chile_-_panoramio.jpg',
      imageAlt: 'Calle Caracoles street in San Pedro de Atacama near cafés',
      imageCredit: 'Wikimedia Commons · panoramio',
      mapsQuery: 'Emporio Andino San Pedro de Atacama Chile'
    },
    {
      id: 'pin-roots',
      name: 'Roots Café',
      category: 'coffee',
      lat: -22.9115,
      lng: -68.2008,
      notes: 'PROPOSED · near Caracoles · optional coffee',
      dayIds: ['2026-11-21'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/Calle_Caracoles%2C_San_Pedro_de_Atacama.JPG/960px-Calle_Caracoles%2C_San_Pedro_de_Atacama.JPG',
      imageAlt: 'Calle Caracoles adobe streetscape near Roots Café area',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Roots Café San Pedro de Atacama Chile'
    },
    {
      id: 'pin-adobe',
      name: 'Café Adobe',
      category: 'coffee',
      lat: -22.9102,
      lng: -68.2014,
      notes: 'PROPOSED · near Caracoles · optional coffee',
      dayIds: ['2026-11-21'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/San_Pedro_de_Atacama_%28street_view%29.jpg/960px-San_Pedro_de_Atacama_%28street_view%29.jpg',
      imageAlt: 'San Pedro de Atacama street view near Café Adobe',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Café Adobe San Pedro de Atacama Chile'
    },
    {
      id: 'pin-el-tatio',
      name: 'El Tatio geysers',
      category: 'attraction',
      lat: -22.3348,
      lng: -68.0131,
      notes: 'PROPOSED · ~4300m · early departure',
      dayIds: ['2026-11-22'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/El_Tatio_geothermal_field_in_Chile.jpg/960px-El_Tatio_geothermal_field_in_Chile.jpg',
      imageAlt: 'Steam rising from El Tatio geyser field in the Atacama high Andes',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'El Tatio geysers Atacama Chile'
    },
    {
      id: 'pin-puritama',
      name: 'Termas de Puritama',
      category: 'attraction',
      lat: -22.7208,
      lng: -68.0447,
      notes: 'PROPOSED · optional hot springs after Tatio',
      dayIds: ['2026-11-22'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Termas_de_Puritama-7_(53624692586).jpg/960px-Termas_de_Puritama-7_(53624692586).jpg',
      imageAlt: 'Terraced hot spring pools at Termas de Puritama',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Termas de Puritama Atacama Chile'
    },
    {
      id: 'pin-chaxa',
      name: 'Laguna Chaxa',
      category: 'attraction',
      lat: -23.3167,
      lng: -68.1667,
      notes: 'PROPOSED · Salar de Atacama flamingos',
      dayIds: ['2026-11-23'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/Laguna_Chaxa%2C_Desierto_de_Atacama%2C_Chile%2C_2016-02-06%2C_DD_37.JPG/960px-Laguna_Chaxa%2C_Desierto_de_Atacama%2C_Chile%2C_2016-02-06%2C_DD_37.JPG',
      imageAlt: 'Flamingos and salt flats at Laguna Chaxa in the Salar de Atacama',
      imageCredit: 'Wikimedia Commons · Diego Delso',
      mapsQuery: 'Laguna Chaxa Salar de Atacama'
    },
    {
      id: 'pin-tebenquiche',
      name: 'Laguna Tebenquiche',
      category: 'attraction',
      lat: -23.1364,
      lng: -68.2561,
      notes: 'PROPOSED · optional salt-flat stop',
      dayIds: ['2026-11-23'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/Laguna_Tebinquinche%2C_desierto_de_Atacama%2C_Chile%2C_2016-02-06%2C_DD_30.jpg/960px-Laguna_Tebinquinche%2C_desierto_de_Atacama%2C_Chile%2C_2016-02-06%2C_DD_30.jpg',
      imageAlt: 'Mirror-like Laguna Tebenquiche (Tebinquinche) in the Atacama salt flats',
      imageCredit: 'Wikimedia Commons · Diego Delso',
      mapsQuery: 'Laguna Tebenquiche Atacama Chile'
    },
    {
      id: 'pin-toconao',
      name: 'Toconao',
      category: 'attraction',
      lat: -23.1905,
      lng: -68.007,
      notes: 'PROPOSED · optional village stop',
      dayIds: ['2026-11-23'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/17/Iglesia_de_San_Lucas%2C_Toconao%2C_Chile%2C_2016-02-06%2C_DD_32.jpg/960px-Iglesia_de_San_Lucas%2C_Toconao%2C_Chile%2C_2016-02-06%2C_DD_32.jpg',
      imageAlt: 'Iglesia de San Lucas in the village of Toconao, Atacama',
      imageCredit: 'Wikimedia Commons · Diego Delso',
      mapsQuery: 'Toconao Atacama Chile'
    },
    {
      id: 'pin-pn',
      name: 'Puerto Natales',
      category: 'transport',
      lat: -51.7236,
      lng: -72.4875,
      notes: 'Bus hub · lodging TBD · overnight Nov 24 proposed before trek · recovery Nov 29',
      dayIds: ['2026-11-24', '2026-11-25', '2026-11-28', '2026-11-29', '2026-11-30'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f7/Puerto_Natales%2C_Chile1.jpg/960px-Puerto_Natales%2C_Chile1.jpg',
      imageAlt: 'Waterfront view of Puerto Natales, gateway to Torres del Paine',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Puerto Natales Chile'
    },
    {
      id: 'pin-pudeto',
      name: 'Pudeto',
      category: 'transport',
      lat: -51.0645,
      lng: -72.978,
      notes: 'Catamaran pier · arrive ~09:30 bus',
      dayIds: ['2026-11-25'],
      mapsQuery: 'Pudeto Torres del Paine Chile'
    },
    {
      id: 'pin-paine-grande',
      name: 'Paine Grande (Vertice)',
      category: 'lodging',
      lat: -51.075,
      lng: -73.068,
      notes: 'Night of 25 Nov · Vertice.travel',
      dayIds: ['2026-11-25'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a8/Paine_Grande_buildings.JPG/960px-Paine_Grande_buildings.JPG',
      imageAlt: 'Paine Grande refugio buildings beside Lake Pehoé',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Refugio Paine Grande Torres del Paine'
    },
    {
      id: 'pin-cuernos',
      name: 'Campsite Cuernos',
      category: 'lodging',
      lat: -50.978,
      lng: -72.966,
      notes: 'Las Torres · 26–27 Nov',
      dayIds: ['2026-11-26', '2026-11-27'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/76/Cuernos_del_Paine_in_Torres_del_Paine_National_Park.jpg/960px-Cuernos_del_Paine_in_Torres_del_Paine_National_Park.jpg',
      imageAlt: 'Iconic Cuernos del Paine horns above the trail corridor',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Campsite Cuernos Torres del Paine'
    },
    {
      id: 'pin-chileno',
      name: 'Campsite Chileno',
      category: 'lodging',
      lat: -50.945,
      lng: -72.92,
      notes: 'Las Torres · 27–28 Nov',
      dayIds: ['2026-11-27', '2026-11-28'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/090115_Refugio_Chileno_Torrres_del_Paine.jpg/960px-090115_Refugio_Chileno_Torrres_del_Paine.jpg',
      imageAlt: 'Refugio Chileno camp buildings in Torres del Paine',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Campsite Chileno Torres del Paine'
    },
    {
      id: 'pin-base-torres',
      name: 'Base Torres viewpoint',
      category: 'attraction',
      lat: -50.9425,
      lng: -72.888,
      notes: 'Iconic towers viewpoint',
      dayIds: ['2026-11-28'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d7/Mirador_Base_Torres_del_Paine.jpg/960px-Mirador_Base_Torres_del_Paine.jpg',
      imageAlt: 'The three towers from Mirador Base Torres',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Base Torres Mirador Torres del Paine'
    },
    {
      id: 'pin-laguna-amarga',
      name: 'Laguna Amarga',
      category: 'transport',
      lat: -50.975,
      lng: -72.78,
      notes: 'Exit bus 20:00 → Puerto Natales',
      dayIds: ['2026-11-28'],
      mapsQuery: 'Laguna Amarga Torres del Paine'
    },
    {
      id: 'pin-cafe-pn',
      name: 'Café Kaiken (placeholder)',
      category: 'coffee',
      lat: -51.7255,
      lng: -72.491,
      notes: 'Editable coffee pin near Puerto Natales plaza',
      dayIds: ['2026-11-29'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Plaza_de_Puerto_Natales%2C_Chile_-_panoramio.jpg/960px-Plaza_de_Puerto_Natales%2C_Chile_-_panoramio.jpg',
      imageAlt: 'Plaza de Armas in Puerto Natales near café stops',
      imageCredit: 'Wikimedia Commons · panoramio',
      mapsQuery: 'Café Kaiken Puerto Natales Chile'
    },
    {
      id: 'pin-food-pn',
      name: 'Asador Patagónico (placeholder)',
      category: 'food',
      lat: -51.722,
      lng: -72.489,
      notes: 'Editable food pin · post-trek dinner idea',
      dayIds: ['2026-11-28', '2026-11-29'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/9/96/Cordero-patagonico.jpg',
      imageAlt: 'Patagonian roast lamb — post-trek dinner idea',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Asador Patagónico Puerto Natales Chile'
    },
    {
      id: 'pin-mirador',
      name: 'Mirador Condor (placeholder)',
      category: 'attraction',
      lat: -51.05,
      lng: -73.02,
      notes: 'Editable attraction near park interior',
      dayIds: ['2026-11-26'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/Condor_and_Lake_Pehoe_Mirador_Condor_Torres_Del_Paine_National_Park_Chile.jpg/960px-Condor_and_Lake_Pehoe_Mirador_Condor_Torres_Del_Paine_National_Park_Chile.jpg',
      imageAlt: 'Lake Pehoé view from Mirador Cóndor in Torres del Paine',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Mirador Cóndor Torres del Paine'
    },
    {
      id: 'pin-cerro-san-cristobal',
      name: 'Cerro San Cristóbal',
      category: 'attraction',
      lat: -33.4253,
      lng: -70.6323,
      notes: 'PROPOSED · Parque Metropolitano · Santiago views',
      dayIds: ['2026-11-16', '2026-11-17'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Virgen_de_la_Inmaculada_Concepci%C3%B3n%2C_cerro_San_Crist%C3%B2bal.jpg/960px-Virgen_de_la_Inmaculada_Concepci%C3%B3n%2C_cerro_San_Crist%C3%B2bal.jpg',
      imageAlt: 'Statue of the Virgin on Cerro San Cristóbal, Santiago',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Cerro San Cristóbal Santiago Chile'
    },
    {
      id: 'pin-mercado-central',
      name: 'Mercado Central',
      category: 'food',
      lat: -33.4378,
      lng: -70.6511,
      notes: 'PROPOSED · seafood market & lunch',
      dayIds: ['2026-11-17'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/Mercado_Central_de_Santiago_%28interior%29_-_panoramio.jpg/960px-Mercado_Central_de_Santiago_%28interior%29_-_panoramio.jpg',
      imageAlt: 'Interior of Mercado Central de Santiago market hall',
      imageCredit: 'Wikimedia Commons · panoramio',
      mapsQuery: 'Mercado Central Santiago Chile'
    },
    {
      id: 'pin-bellas-artes',
      name: 'Museo Nacional de Bellas Artes',
      category: 'attraction',
      lat: -33.4356,
      lng: -70.6439,
      notes: 'PROPOSED · Parque Forestal',
      dayIds: ['2026-11-17'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Museo_Nacional_de_Bellas_Artes%2C_Santiago_20230311.jpg/960px-Museo_Nacional_de_Bellas_Artes%2C_Santiago_20230311.jpg',
      imageAlt: 'Facade of Museo Nacional de Bellas Artes, Santiago',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Museo Nacional de Bellas Artes Santiago'
    },
    {
      id: 'pin-bellavista',
      name: 'Bellavista / Pio Nono',
      category: 'food',
      lat: -33.4327,
      lng: -70.6348,
      notes: 'PROPOSED · casual lunch after Cerro San Cristóbal',
      dayIds: ['2026-11-16'],
      mapsQuery: 'Pio Nono Bellavista Santiago Chile'
    },
    {
      id: 'pin-lastarria',
      name: 'Barrio Lastarria',
      category: 'attraction',
      lat: -33.4395,
      lng: -70.6405,
      notes: 'PROPOSED · cafés, galleries, evening stroll',
      dayIds: ['2026-11-16', '2026-11-17', '2026-12-01'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/ed/Barrio_Lastarria_Santiago.jpg/960px-Barrio_Lastarria_Santiago.jpg',
      imageAlt: 'Street scene in Barrio Lastarria, Santiago',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Barrio Lastarria Santiago Chile'
    },
    {
      id: 'pin-cajon-maipo',
      name: 'Cajón del Maipo',
      category: 'attraction',
      lat: -33.6405,
      lng: -70.353,
      notes: 'PROPOSED · day trip · Embalse El Yeso if accessible',
      dayIds: ['2026-11-18'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/Embalse_El_Yeso%2C_Caj%C3%B3n_del_Maipo_%2839474827444%29.jpg/960px-Embalse_El_Yeso%2C_Caj%C3%B3n_del_Maipo_%2839474827444%29.jpg',
      imageAlt: 'Embalse El Yeso reservoir in Cajón del Maipo canyon',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Cajón del Maipo Chile'
    },
    {
      id: 'pin-casablanca',
      name: 'Casablanca Valley',
      category: 'attraction',
      lat: -33.3208,
      lng: -71.4106,
      notes: 'PROPOSED · optional wine day · not booked',
      dayIds: ['2026-11-17', '2026-11-19'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Across_the_vines_of_pinot_gris_at_Bodegas_RE%2C_Casablanca_Valley%2C_Chile_%2825042417768%29.jpg/960px-Across_the_vines_of_pinot_gris_at_Bodegas_RE%2C_Casablanca_Valley%2C_Chile_%2825042417768%29.jpg',
      imageAlt: 'Pinot gris vineyard rows in Casablanca Valley, Chile',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Casablanca Valley Chile'
    },
    {
      id: 'pin-maipo-wine',
      name: 'Maipo Valley (wine)',
      category: 'attraction',
      lat: -33.65,
      lng: -70.75,
      notes: 'PROPOSED · optional closer wine valley · not booked',
      dayIds: ['2026-11-17', '2026-11-19'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e4/Vi%C3%B1a_De_Martino%2C_Isla_de_Maipo_20240506.jpg/960px-Vi%C3%B1a_De_Martino%2C_Isla_de_Maipo_20240506.jpg',
      imageAlt: 'Viña De Martino vineyard in Isla de Maipo / Maipo Valley',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Maipo Valley Chile wine'
    },
    {
      id: 'pin-puelo',
      name: 'Puelo Specialty Coffee Bar',
      category: 'coffee',
      lat: -33.4067,
      lng: -70.5985,
      notes: 'PROPOSED · Las Catalpas 1522, Vitacura · brunch + specialty coffee',
      dayIds: ['2026-11-15', '2026-11-16', '2026-11-17'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/76/Cup_of_coffee_with_latte_art_2016.jpg/960px-Cup_of_coffee_with_latte_art_2016.jpg',
      imageAlt: 'Latte art coffee cup — Puelo specialty coffee stand-in photo',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Puelo Specialty Coffee Bar Vitacura Chile'
    },
    {
      id: 'pin-bicka',
      name: 'Bicka',
      category: 'coffee',
      lat: -33.3854,
      lng: -70.5750,
      notes: 'PROPOSED · Av. Luis Pasteur 6156, Vitacura · specialty roaster · early weekday hours',
      dayIds: ['2026-11-15', '2026-11-16'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/38/Espresso_machine_in_caf%C3%A9.jpg/960px-Espresso_machine_in_caf%C3%A9.jpg',
      imageAlt: 'Espresso machine in a café — Bicka specialty coffee stand-in',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Bicka Vitacura Chile coffee'
    },
    {
      id: 'pin-super-delicious',
      name: 'Super Delicious',
      category: 'coffee',
      lat: -33.4143,
      lng: -70.6035,
      notes: 'PROPOSED · Av. Vitacura 2839, Las Condes · specialty coffee bar',
      dayIds: ['2026-11-16', '2026-11-17', '2026-11-18'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Cappuccino-cup_20230110_103258.jpg/960px-Cappuccino-cup_20230110_103258.jpg',
      imageAlt: 'Cappuccino cup — Super Delicious coffee stand-in photo',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Super Delicious Vitacura Chile coffee'
    },
    {
      id: 'pin-singular',
      name: 'Singular Coffee Roasters Vitacura',
      category: 'coffee',
      lat: -33.3768,
      lng: -70.5260,
      notes: 'PROPOSED · Av. Las Condes 11700 Loc.102A · Chilean roastery café',
      dayIds: ['2026-11-18', '2026-11-19'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Roasted_coffee_beans.jpg/960px-Roasted_coffee_beans.jpg',
      imageAlt: 'Roasted coffee beans — Singular Coffee Roasters stand-in photo',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Singular Coffee Roasters Vitacura Chile'
    },
    {
      id: 'pin-colmado',
      name: 'Colmado Coffee (Lastarria)',
      category: 'coffee',
      lat: -33.4376,
      lng: -70.6420,
      notes: 'PROPOSED · Merced 346, Barrio Lastarria · specialty coffee & bakery',
      dayIds: ['2026-11-16', '2026-11-17', '2026-11-19'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/35/Latte_art_heart_Garden_Caff%C3%A9_Portugal_20190118.jpg/960px-Latte_art_heart_Garden_Caff%C3%A9_Portugal_20190118.jpg',
      imageAlt: 'Heart latte art — Colmado Coffee stand-in photo',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Colmado Coffee Lastarria Santiago Chile'
    },
    {
      id: 'pin-cafe-trieste',
      name: 'Café Trieste (Lastarria)',
      category: 'coffee',
      lat: -33.4391,
      lng: -70.6415,
      notes: 'PROPOSED · Padre Luis de Valdivia 339, Barrio Lastarria · Illy café',
      dayIds: ['2026-11-16', '2026-11-19', '2026-12-01'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d6/Cappuccino_with_latte_art_on_Coffee_Right_in_Brno%2C_Brno-City_District.jpg/960px-Cappuccino_with_latte_art_on_Coffee_Right_in_Brno%2C_Brno-City_District.jpg',
      imageAlt: 'Cappuccino with latte art — Café Trieste stand-in photo',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Café Trieste Lastarria Santiago Chile'
    },
    {
      id: 'pin-cafe-tdp',
      name: 'Refugio coffee stop (placeholder)',
      category: 'coffee',
      lat: -51.07,
      lng: -73.06,
      notes: 'Editable · near Paine Grande area',
      dayIds: ['2026-11-25'],
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Torres_del_Paine_forest.JPG/960px-Torres_del_Paine_forest.JPG',
      imageAlt: 'Forest trail near Paine Grande refugio coffee stop',
      imageCredit: 'Wikimedia Commons',
      mapsQuery: 'Paine Grande Torres del Paine coffee'
    },
  ],
};
